import { useEffect, useState } from 'react';
import { skipReasonLabels, skipReasons, type SkipReason } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Dialog, RadioCard, TextField } from '../../ds/components';
import { useWorkspace } from '../../lib/session';
import { useTask, type QuickPick, type TaskItem } from '../tasks/api';
import { SnoozeForm, type SnoozeValue } from '../tasks/SnoozePopover';
import styles from './SkipDialog.module.css';

export interface SkipDialogProps {
  open: boolean;
  /** The task of the current entry, or null for an appointment entry (no skip history). */
  task: TaskItem | null;
  quickPicks: ReadonlyArray<QuickPick>;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSkip: (reason: SkipReason, note: string | null) => void;
  /** „Lieber auf später legen?" — null for appointment entries (the API refuses `later` for them). */
  onLater: ((value: SnoozeValue) => void) | null;
  overlayTop?: number | undefined;
  className?: string | undefined;
}

/** „Übersprungen am 22.09. (Kunde nicht erreichbar) und 23.09. (Warte auf Rückmeldung)." */
function skipHistorySentence(labels: string[]): string {
  const stripped = labels.map((label, index) =>
    index === 0 ? label : label.replace(/^Übersprungen am /, ''),
  );
  if (stripped.length === 1) return `${stripped[0] ?? ''}.`;
  return `${stripped.slice(0, -1).join(', ')} und ${stripped[stripped.length - 1] ?? ''}.`;
}

/**
 * Queue 03 „Warum überspringen Sie?" and Queue 04 (third skip): reason tiles, the warning box with the
 * previous skips, the note for the back office (third skip only — Backoffice 02 shows it), „Lieber auf
 * später legen? Später …" and the hand-over footer.
 */
export function SkipDialog({
  open,
  task,
  quickPicks,
  pending,
  error,
  onCancel,
  onSkip,
  onLater,
  overlayTop,
  className,
}: SkipDialogProps) {
  const workspace = useWorkspace();
  const detail = useTask(open && task ? task.id : null);
  const [reason, setReason] = useState<SkipReason>('customer_unreachable');
  const [note, setNote] = useState('');
  const [later, setLater] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason('customer_unreachable');
    setNote('');
    setLater(false);
  }, [open]);

  const skipCount = detail.data?.skipCount ?? task?.skipCount ?? 0;
  const third = task !== null && skipCount >= 2;
  const backOffice = workspace.approvalRecipientName ?? 'das Backoffice';
  const previous = detail.data?.skips.map((entry) => entry.label) ?? [];

  if (later && onLater && task) {
    return (
      <Dialog
        open={open}
        onClose={onCancel}
        width={540}
        eyebrow="Überspringen"
        title="Auf später legen"
        overlayTop={overlayTop}
        className={className}
      >
        <SnoozeForm
          quickPicks={quickPicks}
          initialOn={task.due.on}
          initialTime={task.due.time}
          pending={pending}
          error={error}
          onCancel={() => setLater(false)}
          onSubmit={onLater}
        />
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      width={540}
      overlayTop={overlayTop}
      className={className}
      eyebrow="Überspringen"
      title={third ? 'Diese Task zum dritten Mal überspringen?' : 'Warum überspringen Sie?'}
      subtitle={
        third
          ? `Nach dem dritten Überspringen geht die Task an „Hängt fest“ beim Backoffice. Dort entscheidet ${backOffice}, wer sie übernimmt.`
          : 'Die Task kommt am Ende dieser Session wieder.'
      }
      footerNote={third ? 'Sie bleiben im Verlauf als Verantwortlicher sichtbar.' : undefined}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={pending}>
            Abbrechen
          </Button>
          <Button onClick={() => onSkip(reason, note.trim() === '' ? null : note.trim())} loading={pending}>
            {third ? 'Übergeben an Backoffice' : 'Überspringen'}
          </Button>
        </>
      }
    >
      <div className={styles.stack}>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <div className={styles.reasons} role="radiogroup" aria-label="Grund">
          {skipReasons.map((value) => (
            <RadioCard
              key={value}
              name="skip-reason"
              value={value}
              title={skipReasonLabels[value]}
              checked={reason === value}
              onChange={() => setReason(value)}
            />
          ))}
        </div>
        {third ? (
          <TextField
            label="Notiz"
            optional
            size="compact"
            hint="Für das Backoffice — erscheint bei „Hängt fest“."
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        ) : null}
        {third && previous.length > 0 ? (
          <div className={clsx(styles.box)} role="status">
            <span className={styles.boxTitle}>
              {previous.length === 2
                ? 'Bisher zweimal übersprungen'
                : `Bisher ${previous.length}-mal übersprungen`}
            </span>
            {skipHistorySentence(previous)}
          </div>
        ) : null}
        {onLater ? (
          <div className={styles.laterRow}>
            Lieber auf später legen?
            <button type="button" className={styles.laterLink} onClick={() => setLater(true)}>
              Später …
            </button>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
